import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Eye, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/api-keys/audit')({
  component: ApiKeysAuditPage,
})

interface UserRow {
  userId: string
  email: string
  name: string | null
  apiKeysDisabled: boolean
  keys: Array<{
    id: string
    name: string
    prefix: string
    enabled: boolean
    expiresAt: string | null
    lastRequest: string | null
    createdAt: string
    revokedAt: string | null
  }>
}

function ApiKeysAuditPage() {
  const { data, isLoading, refetch } = useQuery(
    orpc.rbac.apiKeys.listAll.queryOptions({ input: {} }),
  )
  const rows = (data ?? []) as UserRow[]
  const [viewingUser, setViewingUser] = useState<UserRow | null>(null)

  const disableMutation = useMutation(
    orpc.rbac.apiKeys.setUserApiKeysDisabled.mutationOptions({
      onSuccess: () => refetch(),
      onError: (e: Error) => toast.error(e.message),
    }),
  )
  const revokeAllMutation = useMutation(
    orpc.rbac.apiKeys.revokeAllForUser.mutationOptions({
      onSuccess: (result) => {
        toast.success(`Revoked ${(result as { revoked: number }).revoked} keys`)
        refetch()
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">API Keys — Audit</h1>
        <p className="text-muted-foreground">View + disable API key access per user.</p>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-6">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Active keys</TableHead>
                  <TableHead>Disabled</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((u) => {
                  const activeCount = u.keys.filter((k) => !k.revokedAt && k.enabled).length
                  return (
                    <TableRow key={u.userId}>
                      <TableCell>{u.name ?? '-'}</TableCell>
                      <TableCell className="text-muted-foreground">{u.email}</TableCell>
                      <TableCell>{activeCount}</TableCell>
                      <TableCell>
                        <Switch
                          checked={u.apiKeysDisabled}
                          onCheckedChange={(checked) =>
                            disableMutation.mutate({ userId: u.userId, disabled: checked })
                          }
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button size="sm" variant="ghost" onClick={() => setViewingUser(u)}>
                            <Eye className="h-4 w-4 mr-1" />
                            View keys
                          </Button>
                          {activeCount > 0 && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600"
                              onClick={() => revokeAllMutation.mutate({ userId: u.userId })}
                              disabled={revokeAllMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Revoke all
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={viewingUser !== null} onOpenChange={() => setViewingUser(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{viewingUser?.name ?? viewingUser?.email}'s keys</DialogTitle>
          </DialogHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Prefix</TableHead>
                <TableHead>Last used</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(viewingUser?.keys ?? []).map((k) => (
                <TableRow key={k.id}>
                  <TableCell className="font-medium">{k.name}</TableCell>
                  <TableCell className="font-mono text-xs">{k.prefix}…</TableCell>
                  <TableCell className="text-muted-foreground">
                    {k.lastRequest ? new Date(k.lastRequest).toLocaleString() : '—'}
                  </TableCell>
                  <TableCell>
                    {k.revokedAt ? (
                      <Badge variant="outline" className="bg-red-100 text-red-800">
                        Revoked
                      </Badge>
                    ) : !k.enabled ? (
                      <Badge variant="outline" className="bg-amber-100 text-amber-800">
                        Disabled
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-green-100 text-green-800">
                        Active
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </div>
  )
}
